"use client";

import { ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function CollectionGrid() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="bg-card aspect-[4/5] animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <p className="text-sm font-mono uppercase tracking-[0.18em] text-muted-foreground">
        Unable to load collections at this time.
      </p>;
  }
  if (!products || products.length === 0) {
    return <p className="text-sm font-mono uppercase tracking-[0.18em] text-muted-foreground">
        No collections available.
      </p>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
      {products.slice(0, 3).map((product, i) => <article data-index={i} key={product.id} className="group bg-card relative flex flex-col">
          {}
          <Link to={product.link} className="relative block overflow-hidden">
            <div className="aspect-[4/5] overflow-hidden bg-secondary">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[600ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-[1.03]" /> : <div className="h-full w-full bg-secondary" />}
            </div>

            {}
            <span aria-hidden="true" className="pointer-events-none absolute top-3 left-3 w-2 h-2 opacity-0 transition-opacity duration-[240ms] ease-out group-hover:opacity-100">
              <span className="absolute left-1/2 top-0 -translate-x-1/2 w-px h-2 bg-foreground" />
              <span className="absolute top-1/2 left-0 -translate-y-1/2 h-px w-2 bg-foreground" />
            </span>
            <span aria-hidden="true" className="pointer-events-none absolute top-3 right-3 w-2 h-2 opacity-0 transition-opacity duration-[240ms] ease-out group-hover:opacity-100">
              <span className="absolute left-1/2 top-0 -translate-x-1/2 w-px h-2 bg-foreground" />
              <span className="absolute top-1/2 left-0 -translate-y-1/2 h-px w-2 bg-foreground" />
            </span>
            <span aria-hidden="true" className="pointer-events-none absolute bottom-3 left-3 w-2 h-2 opacity-0 transition-opacity duration-[240ms] ease-out group-hover:opacity-100">
              <span className="absolute left-1/2 top-0 -translate-x-1/2 w-px h-2 bg-foreground" />
              <span className="absolute top-1/2 left-0 -translate-y-1/2 h-px w-2 bg-foreground" />
            </span>
            <span aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 w-2 h-2 opacity-0 transition-opacity duration-[240ms] ease-out group-hover:opacity-100">
              <span className="absolute left-1/2 top-0 -translate-x-1/2 w-px h-2 bg-foreground" />
              <span className="absolute top-1/2 left-0 -translate-y-1/2 h-px w-2 bg-foreground" />
            </span>
          </Link>

          {}
          <div className="flex flex-col gap-5 px-6 md:px-8 pt-7 pb-8">
            {}
            <div className="flex items-center justify-between">
              <span className="font-mono text-[0.6875rem] uppercase tracking-[0.18em] text-muted-foreground">
                Edition {String(i + 1).padStart(3, "0")}
              </span>
              <Badge variant="outline" className="font-mono text-[0.6875rem] uppercase tracking-[0.18em] border-border text-muted-foreground">
                {product.categories && product.categories.length > 0 ? product.categories[0].name : "Signature"}
              </Badge>
            </div>

            {}
            <Link to={product.link}>
              <h3 className="font-serif font-default text-2xl md:text-[1.75rem] leading-[1.12] tracking-[-0.015em] text-foreground" style={{
            fontFamily: "var(--font-serif)"
          }}>
                {product.title}
              </h3>
            </Link>

            {}
            <div className="flex items-end justify-between pt-2 border-t border-border">
              <div className="flex flex-col gap-1">
                <span className="font-mono text-[0.6875rem] uppercase tracking-[0.18em] text-muted-foreground">
                  Price
                </span>
                <span className="relative inline-block font-mono text-base text-foreground tracking-[0.04em]">
                  <span className="relative">
                    ${product.price?.toFixed?.(2) ?? product.price}
                    <span aria-hidden="true" className="pointer-events-none absolute left-0 -bottom-1 h-px w-0 group-hover:w-full transition-[width] duration-[360ms] ease-[cubic-bezier(0.7,0,0.2,1)]" style={{
                  backgroundColor: "hsl(15 35% 65%)"
                }} />
                  </span>
                </span>
              </div>
              <span aria-hidden="true" className="block w-2 h-2" style={{
            backgroundColor: "hsl(15 35% 65%)"
          }} />
            </div>

            {}
            <AddToCartButton productId={product.id} variant="default" className="w-full" buttonClassName="bg-primary text-primary-foreground rounded-none h-12 font-mono text-xs uppercase tracking-[0.14em]" text="Acquire" showQuantity={false} />
          </div>
        </article>)}
    </div>;
}
export default function TheSignatureCollections() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="2099" id="the-signature-collections" className="bg-background text-foreground relative py-28 md:py-36 overflow-hidden border-t border-border">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20 relative">
          {}
          <span aria-hidden="true" className="pointer-events-none absolute -top-10 md:-top-16 left-0 md:left-[20%] font-serif text-[10rem] md:text-[14rem] leading-none select-none" style={{
          fontFamily: "var(--font-serif)",
          fontWeight: 300,
          color: "transparent",
          WebkitTextStroke: "0.5px hsl(240 10% 4% / 0.05)"
        }}>
            03
          </span>

          {}
          <div className="md:col-span-5 relative z-10">
            <div className="flex items-center gap-3 mb-5">
              <span aria-hidden="true" className="block w-2 h-2" style={{
              backgroundColor: "hsl(15 35% 65%)"
            }} />
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                § 03 — The Signature Collections
              </span>
            </div>
            <h2 className="font-serif font-default text-[clamp(2.25rem,4.6vw,4.5rem)] leading-[1.02] tracking-[-0.02em] text-foreground" style={{
            fontFamily: "var(--font-serif)",
            fontWeight: 400
          }}>
              Three essences,{" "}
              <em className="italic font-normal">one</em> signature.
            </h2>
          </div>

          {}
          <div className="md:col-span-7 md:pl-8 flex flex-col justify-start pt-2">
            <p className="text-base md:text-lg leading-[1.6] tracking-[-0.003em] text-foreground/85 max-w-[52ch]">
              Each Essence & Co. creation is distilled from rare botanicals, 
              housed in hand-polished glass, and curated for the discerning 
              individual — luxury refined to the final note.
            </p>
            <div className="mt-8">
              <Link to="/ShopPage">
                <Button variant="link" className="px-0 h-auto font-mono text-xs uppercase tracking-[0.14em] text-foreground hover:no-underline group/cta">
                  <span className="relative inline-flex items-center gap-2">
                    <span className="relative">
                      View all collections
                      <span aria-hidden="true" className="absolute left-0 -bottom-1 h-px w-full" style={{
                      backgroundColor: "hsl(15 35% 65%)"
                    }} />
                    </span>
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover/cta:-translate-y-0.5 group-hover/cta:translate-x-0.5" style={{
                    color: "hsl(15 35% 65%)"
                  }} />
                  </span>
                </Button>
              </Link>
            </div>

            {}
            <div className="mt-10 flex items-center gap-2">
              <span aria-hidden="true" className="block w-2 h-2" style={{
              backgroundColor: "hsl(15 35% 65%)"
            }} />
              <span className="block flex-1 h-px bg-border" />
              <span aria-hidden="true" className="block w-2 h-2" style={{
              backgroundColor: "hsl(15 35% 65%)"
            }} />
            </div>
          </div>
        </div>

        {}
        <EcommerceProductsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <CollectionGrid />
        </EcommerceProductsProvider>

        {}
        <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <span className="font-mono text-[0.6875rem] uppercase tracking-[0.18em] text-muted-foreground">
            48.8566° N · 2.3522° E
          </span>
          <span className="font-mono text-[0.6875rem] uppercase tracking-[0.18em] text-muted-foreground">
            Atelier Essence & Co. — Paris
          </span>
        </div>
      </div>
    </section>;
}